import Modal from "@/components/base/Modal";
import { formatDateTime } from "@/lib/format";
import type { AuditLog } from "@/types";
import { getActionMeta, getEntityLabel } from "@/pages/admin/audit/auditMeta";

interface AuditDetailModalProps {
  log: AuditLog | null;
  branchName: string | null;
  onClose: () => void;
}

function ValuePanel({ title, icon, value }: { title: string; icon: string; value: Record<string, unknown> | null }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-background-200 bg-background-50 p-3">
      <p className="flex items-center gap-2 text-xs font-semibold text-foreground-600">
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`${icon} text-sm`}></i>
        </span>
        {title}
      </p>
      {value && Object.keys(value).length > 0 ? (
        <pre className="rkc-scroll-thin max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-background-100 p-3 text-[12px] leading-relaxed text-foreground-800">
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : (
        <p className="rounded-md border border-dashed border-background-300 px-3 py-6 text-center text-xs text-foreground-400">
          Sin datos
        </p>
      )}
    </div>
  );
}

export default function AuditDetailModal({ log, branchName, onClose }: AuditDetailModalProps) {
  if (!log) return null;
  const meta = getActionMeta(log.action);

  const rows: { label: string; value: string; icon: string }[] = [
    { label: "Usuario", value: log.user_name || "Sistema", icon: "ri-user-3-line" },
    { label: "Fecha y hora", value: formatDateTime(log.created_at), icon: "ri-time-line" },
    { label: "Entidad", value: getEntityLabel(log.entity), icon: "ri-stack-line" },
    { label: "Sucursal", value: branchName || "Todas", icon: "ri-building-2-line" },
  ];

  return (
    <Modal
      open={Boolean(log)}
      onClose={onClose}
      size="lg"
      title="Detalle del registro"
      subtitle="Información completa de la acción registrada en la auditoría."
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 p-3.5">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${meta.tone}`}>
            <i className={`${meta.icon} text-lg`}></i>
          </span>
          <div className="min-w-0">
            <p className="font-heading text-sm font-semibold text-foreground-950">{meta.label}</p>
            <p className="truncate font-mono text-xs text-foreground-500">
              {log.entity ? `${log.entity} · ` : ""}
              {log.entity_id || "—"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start gap-2.5 rounded-md border border-background-200 bg-background-50 p-3">
              <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className={`${row.icon} text-sm`}></i>
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
                  {row.label}
                </p>
                <p className="truncate text-sm font-medium text-foreground-900">{row.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ValuePanel title="Valor anterior" icon="ri-history-line" value={log.old_value} />
          <ValuePanel title="Valor nuevo" icon="ri-arrow-right-circle-line" value={log.new_value} />
        </div>
      </div>
    </Modal>
  );
}